import React from 'react';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { Post, Section, P, UL, PostLink, BENCHMARK, Sources, GraderCta } from '~/marketing/Post';
import { said, ranges, one } from '~/marketing/research/figures';

/* What answer engine optimization is, for somebody meeting the term. Approved
   2026-10-13 from `docs/blog/drafts.md`, post 1; every figure is read from the
   benchmark through `figures.js`. */
export default function WhatIsAeo() {
  return (
    <Post path="/blog/what-is-answer-engine-optimization" lede={<>
      Ask an AI assistant who to call about a burst pipe in your city, and the answer names a handful of businesses, often with a reason for each. For the customer reading it, that is the shortlist. Answer engine optimization, or AEO, is the work of getting your business onto that shortlist. You’ll also see it called AI search optimization, AI SEO, or generative engine optimization (GEO).
    </>}>
      <Section title="Where these answers come from">
        <P>
          Customers ask ChatGPT, Claude, Gemini and Perplexity, and Google shows an AI Overview above the ordinary results for many searches. Asked a local question, these AI assistants search the web and write their answer from what they find: directories, review sites, roundup articles, news, and businesses’ own websites. Most of them show the sources they used.
        </P>
      </Section>

      <Section title="How it differs from search engine optimization">
        <P>
          Search engine optimization works toward a ranked list of links. The customer scans the list and chooses where to click.
        </P>
        <P>
          An AI assistant, on the other hand, does the choosing first. It reads the sources and writes a short answer that names a few businesses. In <PostLink to={BENCHMARK}>our benchmark of {said.industries} local industries</PostLink>, an answer to a who-to-hire question named between {one(ranges.perAnswer[0])} and {one(ranges.perAnswer[1])} businesses on average, depending on the industry. That is the whole shortlist the customer sees.
        </P>
        <P>
          The two overlap, because many of the pages an AI assistant reads also rank well in search engine results. They are measured differently, though, so a search engine ranking does not tell you how often you are named in AI answers.
        </P>
      </Section>

      <Section title="Why it matters most to local businesses">
        <P>
          “Who should I call” is a local question, and the answer depends on the city. AI assistants are also ready to name names: in every industry we measured, at least {said.namedAtLeast} in 100 answers to a who-to-hire question named a specific business.
        </P>
        <P>
          So the question for a local business is rarely whether an AI assistant will recommend someone. It is whether it recommends you.
        </P>
      </Section>

      <Section title="What the work involves">
        <UL>
          <li><strong><PostLink to="/blog/get-listed-where-ai-assistants-look">Be listed where AI assistants look.</PostLink></strong> Directories, review sites and roundups are among the sources they cite.</li>
          <li><strong>Get reviews on the sites AI assistants read.</strong> Review sites are among the sources they cite, so reviews there are part of what they read about you.</li>
          <li><strong>Publish pages that answer your customers’ questions.</strong> In our benchmark, {said.own} of the sources cited were businesses’ own websites.</li>
          <li><strong>Use one name everywhere.</strong> AI assistants write a business’s name in many ways, and a consistent name is easier to recognize.</li>
          <li><strong><PostLink to="/blog/how-to-measure-ai-visibility">Measure across repeated answers.</PostLink></strong> The answer changes each time the question is asked, so a reliable reading takes many answers.</li>
        </UL>
      </Section>

      <Sources />
      <GraderCta heading="See which businesses AI assistants name in your city." />
    </Post>
  );
}

WhatIsAeo.layout = withMarketingLayout;
